import type {
  ContentfulButton,
  ContentfulContentPage,
  ContentfulLinkAction,
  ContentfulMicrocopy,
} from "@scott/contentful/types";
import { isScriptedLink, linkActionHref } from "@/utils/link-actions";

function resolveLabel(
  text: string,
  microcopy?: ContentfulMicrocopy | null
): string {
  return microcopy?.value ?? text;
}

/**
 * Internal page slugs carry no market, so they are placed below the active
 * one's base path. A slug without a market is redirected by the storefront,
 * and Imperva serves that redirect from its cache to every visitor regardless
 * of their market cookie. External URLs are never rewritten, even when they
 * point at this site.
 */
function localizeSlug(slug: string, basePath?: string): string {
  if (!basePath) {
    return slug;
  }

  const path = slug.startsWith("/") ? slug : `/${slug}`;
  return path === "/" ? basePath : `${basePath}${path}`;
}

function resolveHref(
  internalPage?: ContentfulContentPage | null,
  externalUrl?: string | null,
  anchor?: string | null,
  basePath?: string
): string {
  if (externalUrl && isScriptedLink(externalUrl)) {
    return linkActionHref(externalUrl) ?? "";
  }

  if (externalUrl) {
    return externalUrl;
  }

  if (internalPage?.slug) {
    return localizeSlug(internalPage.slug, basePath);
  }

  if (anchor) {
    return `#${anchor}`;
  }

  return "";
}

function resolveLinkTracking(
  item: ContentfulLinkAction
): Pick<ContentfulButton, "linkTarget" | "linkType"> {
  if (item.externalUrl) {
    return { linkTarget: item.externalUrl, linkType: "External URL" };
  }

  if (item.internalPage?.slug) {
    return { linkTarget: item.internalPage.slug, linkType: "Internal Page" };
  }

  if (item.anchor) {
    return { linkTarget: item.anchor, linkType: "Anchor" };
  }

  return {};
}

/**
 * @param basePath The active market's `/{country}/{lang}`, which internal page
 * links are placed below.
 */
export function adaptButton(
  item: ContentfulLinkAction,
  basePath?: string
): ContentfulButton {
  return {
    sys: item.sys,
    label: resolveLabel(item.text, item.microcopy),
    href: resolveHref(
      item.internalPage,
      item.externalUrl,
      item.anchor,
      basePath
    ),
    target: item.target ?? undefined,
    variant: item.variant ?? "default",
    ...resolveLinkTracking(item),
  };
}

export function adaptButtons(
  items: Array<ContentfulLinkAction | null> | null | undefined,
  basePath?: string
): ContentfulButton[] {
  if (!items) {
    return [];
  }

  return items
    .filter((item): item is ContentfulLinkAction => item !== null)
    .map((item) => adaptButton(item, basePath))
    .filter((button) => button.href.trim().length > 0);
}
